import { IdentStep, STEP_ID, StepId } from '@/types/ident'
import { IDENTIFICATION_HANDLERS_SWITCH_REASON_REQUEST_IDENTIFICATION_REASON } from '@deepcloud/api-deep-id'

/**
 * Steps allowed to be visited without flow data
 */
export const ALLOWED_STEP_ID = new Set<StepId>([
  STEP_ID.INTRO_DEEP_ID,
  STEP_ID.DEVICE_NOT_SUPPORTED,
  STEP_ID.SCAN_QR_EXPLANATION,
  STEP_ID.SCAN_QR,
  STEP_ID.ENTER_VOUCHER,
  STEP_ID.COUNTRY_SELECTION,
  STEP_ID.COUNTRY_NOT_SUPPORTED,
  STEP_ID.HANDOVER_CONTINUE,
  STEP_ID.ERROR,
  STEP_ID.WAITING,
  STEP_ID.LOGIN,
  STEP_ID.RE_IDENT_STEP_1,
])

export const IDENT_STEPS: IdentStep[] = [
  /**
   *********************************************************************************************************************
   * Setup & Intro
   */
  {
    id: STEP_ID.INTRO_DEEP_ID,
    component: 'SliderStep',
    props: {
      slideType: 'image',
      slides: [
        {
          path: '/logo-deep-id.svg',
          textKey: 'ident.steps.intro.slides.deep_id.title',
          aspectRatio: '75 / 88',
        },
        {
          path: '/svg/intro/intro-passport.svg',
          titleKey: 'ident.steps.intro.slides.intro_passport.title',
          textKey: 'ident.steps.intro.slides.intro_passport.text',
          aspectRatio: '75 / 88',
        },
        {
          path: '/svg/intro/intro-signature.svg',
          titleKey: 'ident.steps.intro.slides.intro_signature.title',
          textKey: 'ident.steps.intro.slides.intro_signature.text',
          aspectRatio: '75 / 88',
        },
        {
          path: '/svg/intro/intro-security.svg',
          titleKey: 'ident.steps.intro.slides.intro_security.title',
          textKey: 'ident.steps.intro.slides.intro_security.text',
          aspectRatio: '75 / 88',
        },
      ],
      // primaryAction is defined in step handler and depending on if coming with a voucher (branchIO) or not
      nextTriggersSlider: false,
      primaryButtonText: 'ident.steps.intro.button_primary',
      secondaryButtonText: 'ident.steps.intro.button_secondary',
    },
  },

  {
    id: STEP_ID.SCAN_QR_EXPLANATION,
    component: 'SliderStep',
    backBehaviour: 'back',
    props: {
      title: 'ident.steps.scan_qr_explanation.title',
      subTitle: 'ident.steps.scan_qr_explanation.sub_title',
      slideType: 'image',
      slides: [
        {
          path: '/svg/intro/scan_qr.svg',
          textKey: 'ident.steps.scan_qr_explanation.slides.qr_code.title',
        },
      ],
      primaryAction: STEP_ID.SCAN_QR,
      secondaryAction: STEP_ID.COUNTRY_SELECTION,
      primaryButtonText: 'ident.steps.scan_qr_explanation.button_primary',
      secondaryButtonText: 'ident.steps.scan_qr_explanation.button_secondary',
    },
  },

  {
    id: STEP_ID.SCAN_QR,
    component: 'ScanQrStep',
    backBehaviour: 'back',
    props: {
      title: 'ident.steps.scan_qr.title',
    },
  },

  {
    id: STEP_ID.ENTER_VOUCHER,
    component: 'EnterVoucherStep',
    backBehaviour: 'back',
    props: {
      title: 'ident.steps.enter_voucher.title',
      subTitle: 'ident.steps.enter_voucher.sub_title',
    },
  },

  {
    id: STEP_ID.DEVICE_NOT_SUPPORTED,
    component: 'DeviceNotSupportedStep',
    props: {
      image: '/svg/exclamation-mark.svg',
      title: 'ident.steps.device_not_supported.title',
    },
  },

  /**
   *********************************************************************************************************************
   * Country Selection
   */
  {
    id: STEP_ID.COUNTRY_SELECTION,
    component: 'CountrySelectionStep',
    backBehaviour: 'back',
    props: {
      title: 'ident.steps.country_selection.title',
      subTitle: 'ident.steps.country_selection.sub_title',
    },
  },

  {
    id: STEP_ID.COUNTRY_NOT_SUPPORTED,
    component: 'InfoActionStep',
    props: {
      image: '/svg/exclamation-mark.svg',
      title: 'ident.steps.country_not_supported.title',
      subTitle: 'ident.steps.country_not_supported.sub_title',
      actions: [
        {
          id: 'back-btn',
          label: 'actions.back',
          variant: 'primary',
          stepId: STEP_ID.COUNTRY_SELECTION,
        },
      ],
    },
  },

  /**
   *********************************************************************************************************************
   * Process Step 1: Scan identity document
   *
   */
  {
    id: STEP_ID.PROCESS_EXPLANATION_1,
    component: 'ProcessExplanationStep',
    backBehaviour: 'back',
    props: {
      title: 'ident.steps.process_explanation.step_1.title',
      subTitle: 'ident.steps.process_explanation.step_1.sub_title',
      sections: [
        {
          path: '/svg/id-passport.svg',
          titleKey: 'ident.steps.process_explanation.step_1.text',
          isActive: true,
        },
        {
          path: '/svg/selfie.svg',
          titleKey: 'ident.steps.process_explanation.step_2.text',
        },
        {
          path: '/svg/personal-details.svg',
          titleKey: 'ident.steps.process_explanation.step_3.text',
        },
      ],
    },
  },

  {
    id: STEP_ID.RE_IDENT_STEP_1,
    component: 'ProcessExplanationStep',
    backBehaviour: 'back',
    props: {
      title: 'reident.process_explanation.title',
      subTitle: 'reident.process_explanation.sub_title',
      activeStep: 1,
      sections: [
        {
          path: '/svg/birthday-cake.svg',
          titleKey: 'reident.process_explanation.step_1',
          isActive: true,
        },
        {
          path: '/svg/selfie.svg',
          titleKey: 'reident.process_explanation.step_2',
        },
        {
          path: '/svg/account-recovery.svg',
          titleKey: 'reident.process_explanation.step_3',
        },
      ],
    },
  },

  {
    id: STEP_ID.DATE_OF_BIRTH,
    component: 'DateOfBirthStep',
    backBehaviour: 'back',
    props: {
      title: 'reident.date_of_birth.title',
      subTitle: 'reident.date_of_birth.sub_title',
    },
  },

  {
    id: STEP_ID.RE_IDENT_STEP_2,
    component: 'ProcessExplanationStep',
    props: {
      title: 'reident.process_explanation.step_2',
      subTitle: 'reident.process_explanation.sub_title',
      activeStep: 2,
      sections: [
        {
          path: '/svg/birthday-cake.svg',
          titleKey: 'reident.process_explanation.step_1',
          isCompleted: true,
        },
        {
          path: '/svg/selfie.svg',
          titleKey: 'reident.process_explanation.step_2',
          isActive: true,
        },
        {
          path: '/svg/account-recovery.svg',
          titleKey: 'reident.process_explanation.step_3',
        },
      ],
    },
  },

  {
    id: STEP_ID.RE_IDENT_STEP_3,
    component: 'ProcessExplanationStep',
    props: {
      title: 'reident.process_explanation.title',
      subTitle: 'reident.process_explanation.sub_title',
      activeStep: 3,
      sections: [
        {
          path: '/svg/birthday-cake.svg',
          titleKey: 'reident.process_explanation.step_1',
          isCompleted: true,
        },
        {
          path: '/svg/selfie.svg',
          titleKey: 'reident.process_explanation.step_2',
          isCompleted: true,
        },
        {
          path: '/svg/account-recovery.svg',
          titleKey: 'reident.process_explanation.step_3',
          isActive: true,
        },
      ],
    },
  },

  {
    id: STEP_ID.RE_IDENT_EXTENDED,
    component: 'InfoActionStep',
    props: {
      image: '/svg/question-mark.svg',
      title: 'reident.extended.title',
      subTitle: 'reident.extended.sub_title',
      supportLink: true,
      actions: [
        {
          id: 'try-again-btn',
          label: 'actions.try_again',
          variant: 'secondary',
          cancel: true,
        },
        {
          id: 'continue-btn',
          label: 'actions.continue',
          variant: 'primary',
          stepId: STEP_ID.COUNTRY_SELECTION,
          data: {
            title: 'reident.country_selection.title',
            subTitle: 'reident.country_selection.sub_title',
          },
          mergeFlow: { extendedReIdentStarted: true },
        },
      ],
    },
  },

  {
    id: STEP_ID.RE_IDENT_CERTIFICATION,
    component: 'InfoActionStep',
    props: {
      image: '/svg/hourglass-expired.svg',
      title: 'reident.certification.title',
      subTitle: 'reident.certification.sub_title',
      infoText: 'reident.certification.processing',
      actions: [
        {
          id: 'cancel-ident-btn',
          label: 'actions.cancel',
          variant: 'secondary',
          cancel: true,
        },
        {
          id: 'contact-support-btn',
          label: 'contact.support_text',
          variant: 'primary',
          href: 'contact.support_url',
        },
      ],
    },
  },

  {
    id: STEP_ID.CERTIFICATION_DECLINED_SOFT,
    component: 'InfoActionStep',
    props: {
      image: '/svg/exclamation-mark.svg',
      title: 'certification.declined_soft.title',
      subTitle: 'certification.declined_soft.sub_title',
      actions: [
        {
          id: 'contact-support-btn',
          label: 'contact.support_text',
          variant: 'secondary',
          href: 'contact.support_url',
        },
        {
          id: 'try-again-btn',
          label: 'actions.try_again',
          variant: 'primary',
          cancel: true,
        },
      ],
    },
  },
  {
    id: STEP_ID.CERTIFICATION_DECLINED_HARD,
    component: 'InfoActionStep',
    props: {
      image: '/svg/hourglass-expired.svg',
      title: 'certification.declined_hard.title',
      subTitle: 'certification.declined_hard.sub_title',
      actions: [
        {
          id: 'cancel-ident-btn',
          label: 'actions.cancel',
          variant: 'secondary',
          cancel: true,
        },
        {
          id: 'contact-support-btn',
          label: 'contact.support_text',
          variant: 'primary',
          href: 'contact.support_url',
        },
      ],
    },
  },
  {
    id: STEP_ID.CERTIFICATION_NO_MATCH,
    component: 'InfoActionStep',
    props: {
      image: '/svg/question-mark.svg',
      title: 'certification.no_match.title',
      subTitle: 'certification.no_match.sub_title',
      actions: [
        {
          id: 'contact-support-btn',
          label: 'contact.support_text',
          variant: 'secondary',
          href: 'contact.support_url',
        },
        {
          id: 'register-btn',
          label: 'actions.register',
          variant: 'primary',
          cancel: true,
        },
      ],
    },
  },

  /**
   *********************************************************************************************************************
   * handover to mobile device, if flow was started on desktop
   */
  {
    id: STEP_ID.HANDOVER,
    component: 'HandoverStep',
    backBehaviour: 'back',
    props: {
      title: 'ident.steps.handover.title',
      subTitle: 'ident.steps.handover.sub_title',
    },
  },
  {
    id: STEP_ID.HANDOVER_CONTINUE,
    component: 'HandoverContinueStep',
    props: {
      title: 'ident.steps.handover.title',
      subTitle: 'ident.steps.handover.sub_title',
    },
  },

  /**
   *********************************************************************************************************************
   * document selection: passport, identity-card or residence-permit, e-id (future)
   */
  {
    id: STEP_ID.DOCUMENT_SELECTION,
    component: 'DocumentSelectionStep',
    props: {
      title: 'ident.steps.document_selection.title',
      subTitle: 'ident.steps.document_selection.sub_title',
    },
  },
  {
    id: STEP_ID.DOCUMENT_RESIDENCE_PERMIT_INFO,
    component: 'DocumentResidencePermitInfoStep',
    backBehaviour: 'back',
    props: {
      title: 'ident.steps.document_residence_permit_info.title',
      subTitle: 'ident.steps.document_residence_permit_info.sub_title',
    },
  },

  /**
   *********************************************************************************************************************
   * passport mrz-images
   */
  {
    id: STEP_ID.PASSPORT_EXPLANATION_MRZ,
    component: 'SliderStep',
    props: {
      title: 'ident.steps.passport_picture_explanation.mrz.title',
      slideType: 'lottie',
      slides: [
        {
          path: '/animations/passport/01_passport_place_surface.json',
          textKey:
            'ident.steps.document_picture_explanation.slide.document_place_surface',
        },
        {
          path: '/animations/passport/02_passport_no_hiding.json',
          textKey:
            'ident.steps.document_picture_explanation.slide.document_no_hiding',
        },
        {
          path: '/animations/passport/03_passport_light.json',
          textKey:
            'ident.steps.document_picture_explanation.slide.document_light',
        },
        {
          path: '/animations/passport/04_passport_picture.json',
          textKey:
            'ident.steps.document_picture_explanation.slide.document_picture',
        },
      ],
      nextStep: STEP_ID.PASSPORT_PICTURE_MRZ,
    },
    backBehaviour: 'back',
  },

  {
    id: STEP_ID.PASSPORT_PICTURE_MRZ,
    component: 'DocumentPictureStep',
    backBehaviour: 'back',
    props: {
      title: 'ident.steps.passport_picture.mrz.title',
      documentOverlay:
        '/svg/document-scanning/document-overlay-passport-mrz.svg',
      documentOverlayRatio: '347 / 258',
    },
  },

  /**
   *********************************************************************************************************************
   * passport signature-images
   */
  {
    id: STEP_ID.PASSPORT_EXPLANATION_SIGNATURE,
    component: 'SliderStep',
    props: {
      title: 'ident.steps.passport_picture_explanation.signature.title',
      slideType: 'lottie',
      slides: [
        {
          path: '/animations/passport/05_passport_signature.json',
          textKey:
            'ident.steps.document_picture_explanation.slide.passport_signature',
        },
      ],
      nextStep: STEP_ID.PASSPORT_PICTURE_SIGNATURE,
    },
  },

  {
    id: STEP_ID.PASSPORT_PICTURE_SIGNATURE,
    component: 'DocumentPictureStep',
    backBehaviour: 'back',
    props: {
      title: 'ident.steps.passport_picture.signature.title',
      documentOverlay:
        '/svg/document-scanning/document-overlay-passport-signature.svg',
      documentOverlayRatio: '347 / 258',
    },
  },

  /**
   *********************************************************************************************************************
   * identity-card mrz-images
   */
  {
    id: STEP_ID.IDENTITY_CARD_EXPLANATION_MRZ,
    component: 'SliderStep',
    props: {
      title: 'ident.steps.identity_card_picture_explanation.back_side.title',
      subTitle:
        'ident.steps.identity_card_picture_explanation.back_side.sub_title',
      slideType: 'lottie',
      slides: [
        {
          path: '/animations/identity-card/01_id_place_surface.json',
          textKey:
            'ident.steps.document_picture_explanation.slide.document_place_surface',
        },
        {
          path: '/animations/identity-card/02_id_no_hiding.json',
          textKey:
            'ident.steps.document_picture_explanation.slide.document_no_hiding',
        },
        {
          path: '/animations/identity-card/03_id_light.json',
          textKey:
            'ident.steps.document_picture_explanation.slide.document_light',
        },
        {
          path: '/animations/identity-card/04_id_picture.json',
          textKey:
            'ident.steps.document_picture_explanation.slide.document_picture',
        },
      ],
      nextStep: STEP_ID.IDENTITY_CARD_PICTURE_MRZ,
    },
  },

  {
    id: STEP_ID.IDENTITY_CARD_PICTURE_MRZ,
    component: 'DocumentPictureStep',
    backBehaviour: 'back',
    props: {
      title: 'ident.steps.identity_card_picture.back_side.title',
      documentType: 'id',
      documentOverlay: '/svg/document-scanning/document-overlay-id-mrz.svg',
      documentOverlayRatio: '347 / 218',
    },
  },

  /**
   *********************************************************************************************************************
   * identity-card front-images
   */
  {
    id: STEP_ID.IDENTITY_CARD_EXPLANATION_FRONT,
    component: 'SliderStep',
    props: {
      title: 'ident.steps.identity_card_picture_explanation.front_side.title',
      subTitle:
        'ident.steps.identity_card_picture_explanation.front_side.sub_title',
      slideType: 'lottie',
      slides: [
        {
          path: '/animations/identity-card/05_id_turn_to_front.json',
          textKey:
            'ident.steps.document_picture_explanation.slide.identity_card_front',
        },
      ],
      nextStep: STEP_ID.IDENTITY_CARD_PICTURE_FRONT,
    },
  },

  {
    id: STEP_ID.IDENTITY_CARD_PICTURE_FRONT,
    component: 'DocumentPictureStep',
    backBehaviour: 'back',
    props: {
      title: 'ident.steps.identity_card_picture.front_side.title',
      documentType: 'id',
      documentOverlay: '/svg/document-scanning/document-overlay-id-front.svg',
      documentOverlayRatio: '347 / 218',
    },
  },

  {
    id: STEP_ID.DOCUMENT_PICTURE_ERROR,
    component: 'DocumentPictureErrorStep',
    props: {},
  },

  /**
   *********************************************************************************************************************
   * passport mrz-feature-challenge
   */
  {
    id: STEP_ID.PASSPORT_EXPLANATION_MRZ_FEATURE_CHALLENGE,
    component: 'SliderStep',
    props: {
      title:
        'ident.steps.document_challenge_explanation.mrz_feature_challenge.title',
      slideType: 'image',
      // slides being added in onEnter handler
      slides: [],
      nextStep: STEP_ID.PASSPORT_MRZ_FEATURE_CHALLENGE,
    },
  },

  {
    id: STEP_ID.PASSPORT_MRZ_FEATURE_CHALLENGE,
    component: 'DocumentChallengeStep',
    props: {
      title: 'ident.steps.document_challenge.title',
      documentOverlay:
        '/svg/document-challenge/doc-auth-challenge-mask-passport.svg',
      documentOverlayRatio: '478 / 520',
    },
  },

  /**
   *********************************************************************************************************************
   * identity-card mrz-feature-challenge
   */
  {
    id: STEP_ID.IDENTITY_CARD_EXPLANATION_MRZ_FEATURE_CHALLENGE,
    component: 'SliderStep',
    props: {
      title:
        'ident.steps.identity_card_challenge_explanation.mrz_feature_challenge.title',
      slideType: 'image',
      // slides being added in onEnter handler
      slides: [],
      nextStep: STEP_ID.IDENTITY_CARD_MRZ_FEATURE_CHALLENGE,
    },
  },

  {
    id: STEP_ID.IDENTITY_CARD_MRZ_FEATURE_CHALLENGE,
    component: 'DocumentChallengeStep',
    props: {
      title: 'ident.steps.document_challenge.title',
      documentOverlay:
        '/svg/document-challenge/doc-auth-challenge-mask-id-back.svg',
      documentOverlayRatio: '478 / 520',
    },
  },

  /**
   *********************************************************************************************************************
   * identity-card front-feature-challenge
   */
  {
    id: STEP_ID.IDENTITY_CARD_EXPLANATION_FRONT_FEATURE_CHALLENGE,
    component: 'SliderStep',
    props: {
      title:
        'ident.steps.identity_card_challenge_explanation.front_feature_challenge.title',
      slideType: 'image',
      // slides being added in onEnter handler
      slides: [],
      nextStep: STEP_ID.IDENTITY_CARD_FRONT_FEATURE_CHALLENGE,
    },
  },

  {
    id: STEP_ID.IDENTITY_CARD_FRONT_FEATURE_CHALLENGE,
    component: 'DocumentChallengeStep',
    props: {
      title: 'ident.steps.document_challenge.title',
      documentOverlay:
        '/svg/document-challenge/doc-auth-challenge-mask-id-front.svg',
      documentOverlayRatio: '478 / 520',
    },
  },

  {
    id: STEP_ID.DOCUMENT_CHALLENGE_ERROR,
    component: 'DocumentChallengeErrorStep',
    props: {},
  },

  /**
   *********************************************************************************************************************
   * Process Step 2: Portrait image & liveness images & video
   */
  {
    id: STEP_ID.PROCESS_EXPLANATION_2,
    component: 'ProcessExplanationStep',
    props: {
      title: 'ident.steps.process_explanation.step_2.title',
      subTitle: 'ident.steps.process_explanation.step_2.sub_title',
      sections: [
        {
          path: '/svg/id-passport.svg',
          titleKey: 'ident.steps.process_explanation.step_1.text',
          isCompleted: true,
        },
        {
          path: '/svg/selfie.svg',
          titleKey: 'ident.steps.process_explanation.step_2.text',
          isActive: true,
        },
        {
          path: '/svg/personal-details.svg',
          titleKey: 'ident.steps.process_explanation.step_3.text',
        },
      ],
    },
  },

  {
    id: STEP_ID.PORTRAIT_EXPLANATION_PICTURE,
    component: 'SliderStep',
    props: {
      title: 'ident.steps.portrait_explanation.step_1.title',
      subTitle: '',
      slideType: 'lottie',
      slides: [
        {
          path: '/animations/selfie/01_3d_selfie.json',
          textKey: 'ident.steps.portrait_explanation.step_1.text',
        },
      ],
      nextStep: STEP_ID.PORTRAIT_PICTURE,
    },
  },

  {
    id: STEP_ID.PORTRAIT_PICTURE,
    component: 'PortraitPictureStep',
    backBehaviour: 'back',
    props: {
      title: 'ident.steps.portrait_picture.title',
    },
  },

  {
    id: STEP_ID.PORTRAIT_PICTURE_ERROR,
    component: 'PortraitPictureErrorStep',
    props: {
      title: 'ident.steps.portrait_picture_error.title',
    },
  },

  {
    id: STEP_ID.PORTRAIT_EXPLANATION_VIDEO,
    component: 'SliderStep',
    props: {
      title: 'ident.steps.portrait_explanation.step_2.title',
      subTitle: '',
      slideType: 'lottie',
      slides: [
        {
          path: '/animations/selfie/02_video_challenge.json',
          textKey: 'ident.steps.portrait_explanation.step_2.text',
        },
      ],
      nextStep: STEP_ID.PORTRAIT_VIDEO_LIVENESS,
    },
  },

  {
    id: STEP_ID.PORTRAIT_VIDEO_LIVENESS,
    component: 'PortraitVideoLivenessStep',
    backBehaviour: 'back',
    props: {
      title: 'ident.steps.portrait_video_liveness.title',
    },
  },

  {
    id: STEP_ID.HANDOVER_RETURN,
    component: 'HandoverReturnStep',
    props: {
      title: 'ident.steps.handover_return.title',
      subTitle: 'ident.steps.handover_return.sub_title',
    },
  },

  /**
   *********************************************************************************************************************
   * Process Step 3: Personal Details
   */

  {
    id: STEP_ID.PROCESS_EXPLANATION_3,
    component: 'ProcessExplanationStep',
    props: {
      title: 'ident.steps.process_explanation.step_3.title',
      subTitle: 'ident.steps.process_explanation.step_3.sub_title',
      sections: [
        {
          path: '/svg/id-passport.svg',
          titleKey: 'ident.steps.process_explanation.step_1.text',
          isCompleted: true,
        },
        {
          path: '/svg/selfie.svg',
          titleKey: 'ident.steps.process_explanation.step_2.text',
          isCompleted: true,
        },
        {
          path: '/svg/personal-details.svg',
          titleKey: 'ident.steps.process_explanation.step_3.text',
          isActive: true,
        },
      ],
    },
  },

  {
    id: STEP_ID.PERSONAL_DETAILS,
    component: 'PersonalDetailsStep',
    backBehaviour: 'back',
    props: {
      title: 'ident.steps.personal_details.title',
      subTitle: 'ident.steps.personal_details.sub_title',
    },
  },

  {
    id: STEP_ID.DISPLAY_NAME,
    component: 'DisplayNameStep',
    backBehaviour: 'back',
    props: {
      title: 'ident.steps.display_name.title',
      subTitle: 'ident.steps.display_name.sub_title',
    },
  },

  {
    id: STEP_ID.CHECK_NAMES,
    component: 'CheckNamesStep',
    backBehaviour: 'back',
    props: {
      title: 'ident.steps.check_names.title',
      subTitle: 'ident.steps.check_names.sub_title',
    },
  },

  {
    id: STEP_ID.EXISTING_IDENTITY,
    component: 'InfoActionStep',
    props: {
      image: '/svg/question-mark.svg',
      title: 'ident.steps.existing_identity.title',
      subTitle: 'ident.steps.existing_identity.sub_title',
      // intentionally no stepId in the actions to keep the next handler working
      actions: [
        {
          id: 'recover-btn',
          label: 'ident.steps.existing_identity.button_recover',
          variant: 'primary',
          data: {
            identificationReason:
              IDENTIFICATION_HANDLERS_SWITCH_REASON_REQUEST_IDENTIFICATION_REASON.RE_IDENTIFICATION,
          },
        },
        {
          id: 'new-btn',
          label: 'ident.steps.existing_identity.button_new',
          variant: 'secondary',
          data: {
            identificationReason:
              IDENTIFICATION_HANDLERS_SWITCH_REASON_REQUEST_IDENTIFICATION_REASON.REGISTRATION,
          },
        },
      ],
    },
  },

  {
    id: STEP_ID.DOMICILE_ADDRESS,
    component: 'DomicileAddressStep',
    props: {
      title: 'ident.steps.domicile_address.title',
      subTitle: 'ident.steps.domicile_address.sub_title',
    },
  },

  {
    id: STEP_ID.PHONE_NUMBER,
    component: 'PhoneNumberStep',
    props: {
      title: 'ident.phone_number.title',
      subTitle: 'ident.phone_number.sub_title',
    },
  },

  {
    id: STEP_ID.PHONE_NUMBER_VERIFY,
    component: 'PhoneNumberVerifyStep',
    props: {
      title: 'ident.steps.phone_number_verify.title',
      subTitle: 'ident.steps.phone_number_verify.sub_title',
    },
  },

  {
    id: STEP_ID.EMAIL_ADDRESS,
    component: 'EmailAddressStep',
    props: {
      title: 'ident.steps.email_address.title',
      subTitle: 'ident.steps.email_address.sub_title',
    },
  },

  {
    id: STEP_ID.EMAIL_ADDRESS_VERIFY,
    component: 'EmailAddressVerifyStep',
    props: {
      title: 'ident.steps.email_address_verify.title',
      subTitle: 'ident.steps.email_address_verify.sub_title',
    },
  },

  {
    id: STEP_ID.RECOVERY_CODE,
    component: 'RecoveryCodeStep',
    props: {
      title: 'ident.steps.recovery_code.title',
      subTitle: 'ident.steps.recovery_code.sub_title',
    },
  },

  {
    id: STEP_ID.RECOVERY_CODE_CHALLENGE,
    component: 'RecoveryCodeChallengeStep',
    props: {
      title: 'reident.recovery_code.title',
      subTitle: 'reident.recovery_code.sub_title',
    },
  },

  {
    id: STEP_ID.RECOVERY_CODE_DECLINED,
    component: 'InfoActionStep',
    props: {
      image: '/svg/exclamation-mark.svg',
      title: 'reident.recovery_code.declined.title',
      subTitle: 'reident.recovery_code.declined.sub_title',
      actions: [
        {
          id: 'contact-support-btn',
          label: 'contact.support_text',
          variant: 'secondary',
          href: 'contact.support_url',
        },
        {
          id: 'cancel-ident-btn',
          label: 'actions.cancel',
          variant: 'primary',
          cancel: true,
        },
      ],
    },
  },

  {
    id: STEP_ID.CASE_CONDITIONS,
    component: 'WaitingStep',
    props: {
      title: 'common.waiting',
    },
  },

  {
    id: STEP_ID.USER_REGISTRATION,
    component: 'UserRegistrationStep',
    props: {
      title: 'ident.steps.user_registration.title',
      subTitle: 'ident.steps.user_registration.text',
    },
  },

  {
    id: STEP_ID.IDENTIFICATION_PENDING,
    component: 'IdentificationPendingStep',
    props: {
      title: 'ident.steps.identification_pending.title',
    },
  },

  /**
   *********************************************************************************************************************
   * General steps
   */
  {
    id: STEP_ID.LOGIN,
    component: 'LoginStep',
    props: {
      title: 'ident.steps.login.title',
      subTitle: 'ident.steps.login.sub_title',
    },
  },

  /**
   *********************************************************************************************************************
   * General steps
   */
  {
    id: STEP_ID.ERROR,
    component: 'ErrorStep',
    props: {},
  },

  {
    id: STEP_ID.WAITING,
    component: 'WaitingStep',
    props: {},
  },
]
